/**
 * Same-origin GIF search client — SPEC §1.2 / NFR-PWA-002.
 * All traffic goes through /api/gif/* so the provider never sees user IPs,
 * the API key stays server-side, and CSP img-src remains 'self'.
 * Chosen GIFs are fetched as bytes and sent through the E2EE attachment path —
 * provider and proxy never learn recipients.
 */

import type { Contact } from "@veto/domain";

export interface GifResult {
  id: string;
  desc: string;
  preview: string;
  full: string;
}

export type GifSearchKind = "gifs" | "stickers" | "featured";

export async function searchGifs(query: string, kind: GifSearchKind = "gifs"): Promise<GifResult[]> {
  const params = new URLSearchParams({ type: kind });
  if (kind !== "featured" && query.trim()) params.set("q", query.trim().slice(0, 80));
  else if (kind === "gifs" && !query.trim()) return [];
  const res = await fetch(`/api/gif/search?${params.toString()}`, {
    headers: { "x-veto-gif-consent": "1" },
    signal: AbortSignal.timeout(8000),
  });
  if (res.status === 503) throw new Error("GIF provider not configured on this deployment");
  if (res.status === 403) throw new Error("GIF search is disabled");
  if (!res.ok) throw new Error(res.status === 502 ? "Provider unreachable — try again" : `Search failed (${res.status})`);
  const data = (await res.json()) as {
    ok?: boolean;
    results?: { id: string; desc: string; preview: string; full: string }[];
  };
  return (data.results ?? [])
    .filter((r) => r.preview && r.full)
    .map((r) => ({ id: r.id, desc: r.desc, preview: r.preview, full: r.full }));
}

/** Download a proxied GIF as a File for the E2EE attachment path. */
export async function fetchGifFile(url: string, fileName: string): Promise<File> {
  const res = await fetch(url, { signal: AbortSignal.timeout(15000) });
  if (!res.ok) throw new Error(`GIF download failed (${res.status})`);
  const blob = await res.blob();
  const type = blob.type || "image/gif";
  const safeName = fileName.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 60) || `gif-${Date.now()}.gif`;
  return new File([blob], safeName, { type });
}

export function gifFileName(result: GifResult): string {
  const ext = /\.webp(\?|$)/i.test(result.full) ? "webp" : "gif";
  const base = (result.desc || result.id || "gif").replace(/[^a-zA-Z0-9_-]+/g, "-").slice(0, 32);
  return `tenor-${base || "gif"}-${result.id.slice(0, 8)}.${ext}`;
}

/** True when the conversation supports attachments (direct chats only for now). */
export function canSendMediaTo(contact: Contact | null, convType: string): boolean {
  return convType !== "group" && !!contact;
}
